import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { authApi } from '../api/auth'
import { usersApi } from '../api/users'
import { useAuthStore } from '../stores/authStore'
import { useUiStore } from '../stores/uiStore'
import { useAccountsStore } from '../stores/accountsStore'
import { Spinner } from '../components/ui/Spinner'

type Step = 'phone' | 'otp'

export function LoginPage() {
  const navigate = useNavigate()
  const { setTokens, setUserId } = useAuthStore()
  const { addToast } = useUiStore()
  const { saveAccount } = useAccountsStore()

  const [step, setStep] = useState<Step>('phone')
  const [phone, setPhone] = useState('')
  const [otpId, setOtpId] = useState('')
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [loading, setLoading] = useState(false)
  const otpRefs = useRef<(HTMLInputElement | null)[]>([])

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!phone.trim()) return
    setLoading(true)
    try {
      const res = await authApi.sendOTP(phone.trim())
      setOtpId(res.otp_id)
      setStep('otp')
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Не удалось отправить код')
    } finally {
      setLoading(false)
    }
  }

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return
    const newOtp = [...otp]
    newOtp[index] = value.slice(-1)
    setOtp(newOtp)
    if (value && index < 5) otpRefs.current[index + 1]?.focus()
  }

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus()
    }
  }

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (text.length === 6) {
      setOtp(text.split(''))
      otpRefs.current[5]?.focus()
    }
  }

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const code = otp.join('')
    if (code.length !== 6) return
    setLoading(true)
    try {
      const res = await authApi.verifyOTP(otpId, code)
      setTokens(res.access_token, res.refresh_token)
      // Save account for multi-account switching
      try {
        localStorage.setItem('access_token', res.access_token)
        const profile = await usersApi.getMe()
        setUserId(profile.id)
        saveAccount({
          id: profile.id,
          displayName: [profile.first_name, profile.last_name].filter(Boolean).join(' ') || profile.username || profile.id,
          username: profile.username,
          avatarUrl: profile.avatar_url,
          accessToken: res.access_token,
          refreshToken: res.refresh_token,
        })
      } catch { /* non-fatal */ }
      navigate('/', { replace: true })
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Неверный код')
      setOtp(['', '', '', '', '', ''])
      otpRefs.current[0]?.focus()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="h-full bg-bg-secondary flex items-center justify-center px-4 overflow-y-auto">
      <div className="w-full max-w-sm animate-fade-in">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-accent mb-4">
            <span className="text-4xl text-white select-none">G</span>
          </div>
          <h1 className="text-2xl font-semibold text-txt-primary">GGRAM</h1>
          <p className="text-txt-secondary text-sm mt-1">Безопасный кроссплатформенный мессенджер</p>
        </div>

        <div className="bg-bg-primary rounded-2xl p-6 shadow-xl">
          {step === 'phone' ? (
            <form onSubmit={handlePhoneSubmit} className="space-y-4">
              <div>
                <label className="block text-txt-secondary text-sm mb-2">
                  Номер телефона или email
                </label>
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+7 999 123 45 67"
                  className="w-full bg-bg-input text-txt-primary rounded-xl px-4 py-3 outline-none
                             focus:ring-2 focus:ring-accent placeholder-txt-meta text-base"
                  autoFocus
                  disabled={loading}
                />
              </div>
              <button
                type="submit"
                disabled={loading || !phone.trim()}
                className="w-full bg-accent hover:bg-blue-500 disabled:opacity-50
                           text-white font-medium rounded-xl py-3 transition-colors flex items-center justify-center gap-2"
              >
                {loading ? <Spinner size={20} /> : 'Продолжить'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleOtpSubmit} className="space-y-6">
              <div className="text-center">
                <p className="text-txt-secondary text-sm">
                  Введите код, отправленный на номер
                </p>
                <p className="text-txt-primary font-medium">{phone}</p>
                <button
                  type="button"
                  onClick={() => { setStep('phone'); setOtpId(''); setOtp(['', '', '', '', '', '']) }}
                  className="text-accent text-sm mt-1 hover:underline"
                >
                  Изменить
                </button>
              </div>

              <div className="flex gap-2 justify-center" onPaste={handleOtpPaste}>
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => { otpRefs.current[i] = el }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    className="w-12 h-14 text-center text-xl font-semibold bg-bg-input text-txt-primary
                               rounded-xl outline-none focus:ring-2 focus:ring-accent caret-transparent"
                    autoFocus={i === 0}
                    disabled={loading}
                  />
                ))}
              </div>

              <button
                type="submit"
                disabled={loading || otp.join('').length !== 6}
                className="w-full bg-accent hover:bg-blue-500 disabled:opacity-50
                           text-white font-medium rounded-xl py-3 transition-colors flex items-center justify-center gap-2"
              >
                {loading ? <Spinner size={20} /> : 'Войти'}
              </button>

              <button
                type="button"
                onClick={handlePhoneSubmit}
                disabled={loading}
                className="w-full text-accent text-sm hover:underline disabled:opacity-50"
              >
                Отправить код повторно
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
